import React from 'react';
import { Calculator, Award } from 'lucide-react';
import { CollapsibleCard, CollapseControl } from './CollapsibleCard';
import { SubtestScores, CompositeScores } from '../types';
import { subtestNames, subtestDomains, compositeNames, domainColors, domainColorAlpha } from '../utils/constants';
import {
  getScoreInterpretation,
  getPercentile,
  validateSubtestScore,
  validateCompositeScore,
  getScoreValidationError,
} from '../utils/helpers';

interface SubtestScoreInputProps {
  scores: SubtestScores;
  onChange: (subtest: string, value: string) => void;
  collapse?: CollapseControl;
}

export const SubtestScoreInput: React.FC<SubtestScoreInputProps> = ({ scores, onChange, collapse }) => {
  return (
    <CollapsibleCard
      title="Wyniki Podtestów (Skale standardowe 1-19)"
      icon={Calculator}
      open={collapse?.open}
      onOpenChange={collapse?.onOpenChange}
    >
      <div className="grid grid-cols-2 gap-3.5">
        {Object.entries(subtestNames).map(([key, name]) => {
          const valStr = scores[key] || '';
          const val = parseInt(valStr);
          const isInvalid = valStr !== '' && !validateSubtestScore(valStr);
          const validationError = getScoreValidationError(valStr, 'subtest');
          const hasVal = !isInvalid && !isNaN(val) && val > 0;
          const pct = hasVal ? getPercentile(val, 'subtest') : null;
          const interpretation = hasVal ? getScoreInterpretation(val, 'subtest') : '';
          const color = domainColors[subtestDomains[key]] || '#6B7280';

          return (
            <div key={key} className={`flex items-center gap-2.5 p-2 rounded-xl bg-surface-subtle border transition-colors duration-200 ${isInvalid ? 'border-red-300 dark:border-red-800' : 'border-brd hover:border-brd-strong'}`}>
              <div
                className="w-1 h-8 rounded-full transition-shadow duration-300"
                style={{
                  backgroundColor: color,
                  boxShadow: hasVal ? `0 0 8px ${domainColorAlpha(color, 25)}` : 'none'
                }}
              />
              <div className="flex-1 min-w-0">
                <label className="block text-[11px] font-semibold text-txt-secondary truncate mb-1">
                  {name}
                </label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min="1"
                    max="19"
                    value={valStr}
                    onChange={(e) => onChange(key, e.target.value)}
                    placeholder="--"
                    aria-invalid={isInvalid}
                    className={`w-16 bg-surface-card border rounded-lg px-2 py-1 text-xs text-txt-primary text-center focus:outline-none focus:ring-4 transition-colors duration-200 ${
                      isInvalid
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/10'
                        : 'border-brd focus:border-accent focus:ring-emerald-500/5'
                    }`}
                  />
                  {isInvalid && (
                    <span className="text-[9px] font-semibold text-red-600 dark:text-red-400 leading-tight">
                      {validationError}
                    </span>
                  )}
                  {hasVal && (
                    <div className="flex flex-col text-[9px] text-txt-secondary truncate">
                      <span className="font-semibold text-accent-strong">{pct}% centyl</span>
                      <span className="text-[8px] italic">{interpretation}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </CollapsibleCard>
  );
};

interface CompositeScoreInputProps {
  scores: CompositeScores;
  onChange: (composite: string, value: string) => void;
  collapse?: CollapseControl;
}

export const CompositeScoreInput: React.FC<CompositeScoreInputProps> = ({ scores, onChange, collapse }) => {
  return (
    <CollapsibleCard
      title="Wskaźniki Złożone (Skale ilorazowe 40-160)"
      icon={Award}
      open={collapse?.open}
      onOpenChange={collapse?.onOpenChange}
    >
      <div className="flex flex-col gap-3">
        {Object.entries(compositeNames).map(([key, name]) => {
          const valStr = scores[key] || '';
          const val = parseInt(valStr);
          const isInvalid = valStr !== '' && !validateCompositeScore(valStr);
          const validationError = getScoreValidationError(valStr, 'composite');
          const hasVal = !isInvalid && !isNaN(val) && val > 0;
          const pct = hasVal ? getPercentile(val, 'composite') : null;
          const interpretation = hasVal ? getScoreInterpretation(val, 'composite') : '';
          const color = domainColors[key] || '#6B7280';

          return (
            <div key={key} className={`flex items-center gap-3.5 p-2.5 rounded-xl bg-surface-subtle border transition-colors duration-200 ${isInvalid ? 'border-red-300 dark:border-red-800' : 'border-brd hover:border-brd-strong'}`}>
              <div
                className="w-1.5 h-10 rounded-full transition-shadow duration-300"
                style={{
                  backgroundColor: color,
                  boxShadow: hasVal ? `0 0 10px ${domainColorAlpha(color, 25)}` : 'none'
                }}
              />
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-semibold text-txt-secondary truncate mb-1">
                  {name}
                </label>
                <div className="flex items-center justify-between gap-4">
                  <input
                    type="number"
                    min="40"
                    max="160"
                    value={valStr}
                    onChange={(e) => onChange(key, e.target.value)}
                    placeholder="Wprowadź wynik..."
                    aria-invalid={isInvalid}
                    className={`w-full bg-surface-card border rounded-lg px-3 py-1.5 text-xs text-txt-primary focus:outline-none focus:ring-4 transition-colors duration-200 ${
                      isInvalid
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/10'
                        : 'border-brd focus:border-accent focus:ring-emerald-500/5'
                    }`}
                  />
                  {isInvalid && (
                    <span className="text-[11px] font-semibold text-red-600 dark:text-red-400 shrink-0 min-w-[90px] text-right">
                      {validationError}
                    </span>
                  )}
                  {hasVal && (
                    <div className="flex flex-col text-[11px] text-txt-secondary items-end shrink-0 min-w-[90px]">
                      <span className="font-semibold text-accent-strong">{pct}% centyl</span>
                      <span className="text-[9px] italic text-right">{interpretation}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </CollapsibleCard>
  );
};

